// BESS detail card for SLD detail panel — Gamma design system
import { StatusBadge } from './shared/StatusBadge';
import { ProgressBar } from './shared/ProgressBar';
import { MetricQuadGrid } from './shared/MetricQuadGrid';
import { SOCDonut } from './shared/SOCDonut';
import { DeviceConditionSections } from './shared/DeviceConditionSections';
import type { QuadMetric } from './shared/MetricQuadGrid';
import { formatVoltage } from '../../../utils/formatVoltage';

interface BESSDetailCardProps {
  siteId?: string;
  data: Record<string, unknown>;
  onViewDetails: () => void;
}

function num(v: unknown): number | null {
  if (v == null) return null;
  const n = Number(v);
  return isFinite(n) ? n : null;
}

function fmt(v: unknown, decimals = 1): string {
  if (v == null) return '--';
  const n = Number(v);
  if (!isFinite(n)) return '--';
  return n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

export function BESSDetailCard({ siteId, data, onViewDetails }: BESSDetailCardProps) {
  const label = String(data.label ?? 'Battery Storage');
  const rawMode = String(data.mode ?? data.blockMode ?? data.state ?? data.blockStatus ?? data.status ?? 'standby').toLowerCase();
  const deviceId = data.deviceId as string | undefined;

  const soc = num(data.stateOfCharge);
  const activePower = num(data.activePowerKw);
  const packVoltage = num(data.packVoltageV ?? data.dcBusVoltageV);
  const packCurrent = num(data.packCurrentA ?? data.dcBusCurrentA);
  const temperature = num(data.batteryTemperature ?? data.cellTempMax);
  const stateOfHealth = num(data.stateOfHealth);
  const maxCharge = num(data.maxChargeKw ?? data.maxPowerKw);
  const maxDischarge = num(data.maxDischargeKw);
  const racksOnline = num(data.racksOnline);
  const racksTotal = num(data.racksTotal);
  const pcsState = data.pcsStatus as string | undefined;

  // Normalize mode — EMS may report "On" or operational modes like "MaxEfficiency"
  // If mode isn't explicitly a charge/discharge/idle/off/fault state, infer from power
  let mode = rawMode;
  const isExplicitState = ['charging', 'charge', 'discharging', 'discharge', 'idle', 'off', 'standby', 'fault', 'faulted', 'offline'].includes(mode);
  if (!isExplicitState && activePower != null) {
    if (activePower < -0.5) mode = 'charging';
    else if (activePower > 0.5) mode = 'discharging';
    else mode = 'idle';
  }

  // Derive operational mode display
  const modeDisplay = mode === 'idle' || mode === 'off' || mode === 'standby'
    ? 'Standby'
    : mode.charAt(0).toUpperCase() + mode.slice(1);

  // Efficiency placeholder (no real-time value from telemetry)
  const efficiency = num(data.roundTripEfficiencyPct ?? data.blockEfficiency);

  const quadMetrics: QuadMetric[] = [
    { label: 'Voltage', value: packVoltage != null ? formatVoltage(packVoltage, 'dc') : null, icon: '\u26A1' },
    { label: 'Current', value: packCurrent, unit: 'A', icon: '\uD83D\uDD0C' },
    { label: 'Temp', value: temperature, unit: '\u00B0C', icon: '\uD83C\uDF21\uFE0F' },
    { label: 'Efficiency', value: efficiency, unit: '%', icon: '\u267B\uFE0F' },
  ];

  // Available capacity based on max charge power
  const availableCapacity = maxCharge ?? maxDischarge;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-ems-border flex-shrink-0">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-semibold text-ems-text-primary truncate">{label}</h3>
          <span className="text-xs text-ems-text-muted font-mono">
            {racksOnline != null && racksTotal != null ? `${racksOnline}/${racksTotal}` : ''}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-ems-text-muted">Energy Storage</span>
          <StatusBadge status={modeDisplay} />
        </div>
        {deviceId && (
          <span className="text-[10px] text-ems-text-muted font-mono mt-0.5 block opacity-60">{deviceId}</span>
        )}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {/* SOC Donut */}
        <div className="flex flex-col items-center py-1">
          <SOCDonut soc={soc} size={96} label="SOC" />
          <span className="text-xs text-ems-text-secondary mt-1.5 capitalize">{modeDisplay}</span>
        </div>

        {/* Power Flow */}
        <div className="rounded-xl bg-ems-bg p-3">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider text-ems-text-secondary">Power Flow</span>
            <span className="text-base font-mono font-semibold text-ems-text-primary transition-all duration-300">
              {fmt(activePower, 0)} <span className="text-[10px] font-normal text-ems-text-muted">kW</span>
            </span>
          </div>
        </div>

        {/* Quad Grid */}
        <MetricQuadGrid metrics={quadMetrics} />

        {/* Available Capacity */}
        {availableCapacity != null && (
          <div className="rounded-xl bg-ems-bg p-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs uppercase tracking-wider text-ems-text-secondary">Available Capacity</span>
              <span className="text-xs font-mono text-ems-text-primary transition-all duration-300">
                {fmt(availableCapacity, 0)} kW
              </span>
            </div>
            <ProgressBar
              value={availableCapacity}
              max={availableCapacity}
              colorScheme="inverted"
              height="h-1"
            />
          </div>
        )}

        {/* PCS Status */}
        <div className="rounded-xl bg-ems-bg p-3">
          <span className="text-xs uppercase tracking-wider text-ems-text-secondary block mb-2">PCS Status</span>
          <div className="space-y-1.5">
            {pcsState && (
              <div className="flex justify-between items-center">
                <span className="text-[11px] text-ems-text-muted">State</span>
                <span className="text-[11px] font-mono text-ems-text-secondary capitalize">{pcsState}</span>
              </div>
            )}
            {maxCharge != null && (
              <div className="flex justify-between items-center">
                <span className="text-[11px] text-ems-text-muted">Charge</span>
                <span className="text-[11px] font-mono text-ems-green">{fmt(maxCharge, 0)} kW</span>
              </div>
            )}
            {maxDischarge != null && (
              <div className="flex justify-between items-center">
                <span className="text-[11px] text-ems-text-muted">Discharge</span>
                <span className="text-[11px] font-mono text-ems-blue">{fmt(maxDischarge, 0)} kW</span>
              </div>
            )}
            {stateOfHealth != null && (
              <div className="flex justify-between items-center">
                <span className="text-[11px] text-ems-text-muted">SOH</span>
                <span className="text-[11px] font-mono text-ems-text-secondary">{fmt(stateOfHealth, 0)}%</span>
              </div>
            )}
          </div>
        </div>

        <DeviceConditionSections
          siteId={siteId}
          deviceId={deviceId}
          data={data}
        />
      </div>

      {/* View Details Button */}
      <div className="px-4 py-3 border-t border-ems-border flex-shrink-0">
        <button
          onClick={onViewDetails}
          className="w-full py-2 px-4 bg-ems-forest border border-[rgba(52,211,153,0.2)] rounded-lg text-sm text-ems-green font-medium transition-colors hover:bg-ems-forest/80 hover:border-ems-border-bright"
        >
          View Details &rarr;
        </button>
      </div>
    </div>
  );
}
